import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Divider,
} from "@mui/material";
import { commonStyles } from "../../../assets/MUIStyles/commonStyles";
import moment from "moment";
import { gatewayConfigStyles } from "../../../assets/MUIStyles/gatewayConfig";

const ScheduleModalBody = ({ obj }) => {
  const {
    startTimes,
    setStartTimes,
    endTimes,
    setEndTimes,
    isAllDaySame,
    setIsAllDaySame,
    defaultStartTime,
    setDefaultStartTime,
    defaultEndTime,
    setDefaultEndTime,
  } = obj;

  const [days, setDays] = useState<string[]>([]);
  const [initialized, setInitialized] = useState(false);
  const [individualTimes, setIndividualTimes] = useState({
    startTimes: Array(7).fill(""),
    endTimes: Array(7).fill(""),
  });

  const week = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const dayIndexMap: Record<string, number> = {
    Mon: 0,
    Tue: 1,
    Wed: 2,
    Thu: 3,
    Fri: 4,
    Sat: 5,
    Sun: 6,
  };

  const extractTime = (dateTimeStr: string | null) =>
    dateTimeStr ? moment.utc(dateTimeStr).local().format("HH:mm") : "";

  const formatToDateTime = (time: string | null) =>
    time
      ? moment(time, "HH:mm")
          .set({
            year: moment().year(),
            month: moment().month(),
            date: moment().date(),
          })
          .utc()
          .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]")
      : null;

  useEffect(() => {
    if (!initialized && (startTimes?.length > 0 || endTimes?.length > 0)) {
      const newIndividualTimes = {
        startTimes: Array(7).fill(""),
        endTimes: Array(7).fill(""),
      };
      const selectedDays: string[] = [];

      week.forEach((day, index) => {
        if (startTimes?.[index] || endTimes?.[index]) {
          if (startTimes?.[index]) {
            newIndividualTimes.startTimes[index] = extractTime(
              startTimes[index]
            );
          }
          if (endTimes?.[index]) {
            newIndividualTimes.endTimes[index] = extractTime(endTimes[index]);
          }
          selectedDays.push(day);
        }
      });

      setIndividualTimes(newIndividualTimes);

      if (selectedDays.length === 7) {
        setIsAllDaySame(true);
        setDefaultStartTime(newIndividualTimes.startTimes[0]);
        setDefaultEndTime(newIndividualTimes.endTimes[0]);
      } else {
        setIsAllDaySame(false);
        setDays(selectedDays);
      }

      setInitialized(true);
    }
  }, [startTimes, endTimes, initialized]);

  const handleScheduleTypeChange = (event) => {
    setIsAllDaySame(event.target.value === "all");
  };

  const handleIndividualTimeChange = (dayIndex, timeValue, isStart) => {
    setIndividualTimes((prev) => {
      const updated = { ...prev };
      if (isStart) updated.startTimes[dayIndex] = timeValue;
      else updated.endTimes[dayIndex] = timeValue;
      return updated;
    });
  };

  // Update parent state when things change
  useEffect(() => {
    if (!initialized) return;

    if (isAllDaySame) {
      setStartTimes(Array(7).fill(formatToDateTime(defaultStartTime)));
      setEndTimes(Array(7).fill(formatToDateTime(defaultEndTime)));
    } else {
      const newStartTimes = Array(7).fill(null);
      const newEndTimes = Array(7).fill(null);

      days.forEach((day) => {
        const idx = dayIndexMap[day];
        newStartTimes[idx] = individualTimes?.startTimes[idx]
          ? formatToDateTime(individualTimes?.startTimes[idx])
          : null;
        newEndTimes[idx] = individualTimes?.endTimes[idx]
          ? formatToDateTime(individualTimes?.endTimes[idx])
          : null;
      });

      setStartTimes(newStartTimes);
      setEndTimes(newEndTimes);
    }
  }, [
    isAllDaySame,
    defaultStartTime,
    defaultEndTime,
    individualTimes,
    days,
    initialized,
  ]);

  return (
    <Box>
      <Typography className="font-14-grey">Schedule Type</Typography>
      <RadioGroup
        value={isAllDaySame ? "all" : "custom"}
        onChange={handleScheduleTypeChange}
        row
        sx={{ gap: 2, alignItems: "center" }}
      >
        <FormControlLabel
          value="all"
          control={
            <Radio
              sx={{
                color: "#ff5d17",
                "&.Mui-checked": { color: "#ff5d17" },
              }}
            />
          }
          label="All Day Same"
          sx={{
            "& .MuiFormControlLabel-label": {
              fontSize: "13px",
              fontWeight: 500,
            },
          }}
        />
        <FormControlLabel
          value="custom"
          control={
            <Radio
              sx={{
                color: "#ff5d17",
                "&.Mui-checked": { color: "#ff5d17" },
              }}
            />
          }
          label="Custom"
          sx={{
            "& .MuiFormControlLabel-label": {
              fontSize: "13px",
              fontWeight: 500,
            },
          }}
        />
      </RadioGroup>

      {isAllDaySame ? (
        <Box display="flex" alignItems="center" gap={2} width="100%">
          <Box flex={1}>
            <Typography className="font-14-grey mb-1">Start Time</Typography>
            <TextField
              type="time"
              value={defaultStartTime || ""}
              onChange={(e) => setDefaultStartTime(e.target.value)}
              size="small"
              fullWidth
              sx={{ ...commonStyles.inputField }}
            />
          </Box>
          <Typography sx={{ mt: 3, minWidth: "20px", textAlign: "center" }}>
            to
          </Typography>
          <Box flex={1}>
            <Typography className="font-14-grey mb-1">End Time</Typography>
            <TextField
              type="time"
              value={defaultEndTime || ""}
              onChange={(e) => setDefaultEndTime(e.target.value)}
              size="small"
              fullWidth
              sx={{ ...commonStyles.inputField }}
            />
          </Box>
        </Box>
      ) : (
        <Box mt={2}>
          <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
            {week.map((day) => (
              <Box
                key={day}
                onClick={() =>
                  setDays((prev) =>
                    prev.includes(day)
                      ? prev.filter((d) => d !== day)
                      : [...prev, day]
                  )
                }
                sx={{
                  backgroundColor: days.includes(day)
                    ? "#ff5d17"
                    : "transparent",
                  color: days.includes(day) ? "#fff" : "#000",
                  "&:hover": {
                    backgroundColor: days.includes(day)
                      ? "#e64a19"
                      : "rgba(255,77,0,0.08)",
                  },
                  ...gatewayConfigStyles.weekSelector,
                }}
              >
                {day}
              </Box>
            ))}
          </Box>

          <Box display="flex" flexDirection="column" gap={1.5}>
            {days.map((day, idx) => {
              const dayIndex = dayIndexMap[day];
              return (
                <Box key={day}>
                  <Box display="flex" alignItems="center" gap={2} py={1}>
                    <Typography
                      sx={{
                        minWidth: "50px",
                        fontSize: "13px",
                        fontWeight: 500,
                      }}
                    >
                      {day}
                    </Typography>

                    <Box flex={1}>
                      <TextField
                        type="time"
                        value={individualTimes.startTimes[dayIndex] || ""}
                        onChange={(e) =>
                          handleIndividualTimeChange(
                            dayIndex,
                            e.target.value,
                            true
                          )
                        }
                        size="small"
                        fullWidth
                        sx={{ ...commonStyles.inputField }}
                      />
                    </Box>

                    <Typography
                      sx={{
                        minWidth: "20px",
                        textAlign: "center",
                        fontSize: "13px",
                      }}
                    >
                      to
                    </Typography>

                    <Box flex={1}>
                      <TextField
                        type="time"
                        value={individualTimes.endTimes[dayIndex] || ""}
                        onChange={(e) =>
                          handleIndividualTimeChange(
                            dayIndex,
                            e.target.value,
                            false
                          )
                        }
                        size="small"
                        fullWidth
                        sx={{ ...commonStyles.inputField }}
                      />
                    </Box>
                  </Box>
                  {idx < days.length - 1 && <Divider sx={{ opacity: 0.3 }} />}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ScheduleModalBody;
